import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  Injector,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { FieldTree } from '@angular/forms/signals';
import { AccordionComponent } from '../../accordion/accordion.component';
import { TextareaComponent } from '../textarea/textarea.component';
import { MobilePortraitService } from '../../../../services/mobile-portrait.service';
import { PresenceAvatarsComponent } from '../../presence-avatars/presence-avatars.component';
import { HubUser } from '../../../checklist/types/generated/checklist-models';
import { prefersReducedMotion } from '../../../../utils/scroll-into-view.utils';

const EMPTY_VIEWERS: readonly HubUser[] = [];

@Component({
  selector: 'app-remarks',
  imports: [AccordionComponent, TextareaComponent, PresenceAvatarsComponent],
  templateUrl: './remarks.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Keeps a caller's static `id` attribute off the host element, which would
  // otherwise duplicate the `id` passed through to the inner `app-textarea`.
  host: { '[attr.id]': 'null' },
})
export class RemarksComponent {
  readonly field = input.required<FieldTree<string>>();
  readonly id = input('');
  readonly label = input('Remarks');
  readonly rows = input(5);
  readonly placeholder = input('');
  readonly containerClass = input('');
  readonly textareaClass = input('');
  // Set by `app-field-host` whenever field presence changes, since this field's own control is not
  // always rendered to carry the highlight.
  readonly presenceViewers = input<readonly HubUser[]>([]);

  private readonly textarea = viewChild<TextareaComponent>('textarea');

  private readonly injector = inject(Injector);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly accordionOpen = signal(false);

  readonly isMobilePortrait = inject(MobilePortraitService).isMobilePortrait;

  protected readonly state = computed(() => this.field()());

  // Only while the accordion is closed because once it is open, the textarea inside carries the highlight
  // like any other text field, and circles in the header on top of that would say it twice.
  readonly headerViewers = computed<readonly HubUser[]>(() =>
    this.accordionOpen() ? EMPTY_VIEWERS : this.presenceViewers()
  );

  onAccordionOpenChange(open: boolean): void {
    const wasClosed = !this.accordionOpen();
    this.accordionOpen.set(open);

    if (!open || !wasClosed) {
      return;
    }

    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';

    // Focus must run synchronously in the tap gesture or mobile engines keep the keyboard closed.
    // Scrolling stays deferred until the revealed content has laid out.
    this.changeDetectorRef.detectChanges();
    this.textarea()?.focus();

    afterNextRender(
      () => {
        this.textarea()?.scrollIntoView({ block: 'end', behavior });
      },
      { injector: this.injector }
    );
  }

  get title(): string {
    if (this.isMobilePortrait()) {
      return 'Add Remarks';
    }

    return this.label();
  }

  get badgeIcon(): string | null {
    const value = this.state().value();
    if (typeof value !== 'string') {
      return null;
    }

    return value.trim().length > 0 ? 'assets/svg/message.svg' : null;
  }
}
